import { useState, useEffect } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Lock } from "lucide-react";
import { useMFAReminder } from "@/contexts/MFAReminderContext";
import { ProfileSecurityForm } from "@/components/profile/ProfileSecurityForm";
import { useUserMFA } from "@/hooks/profile/useUserMFA";

interface MFAReminderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function MFAReminderDialog({
  open,
  onOpenChange,
}: MFAReminderDialogProps) {
  const { dismissReminder } = useMFAReminder();
  const { checkMFAStatus } = useUserMFA();
  const [showSetup, setShowSetup] = useState(false);
  const [mfaStatus, setMfaStatus] = useState({
    enabled: false,
    verified: false,
  });

  // Check MFA status when dialog opens
  useEffect(() => {
    if (open) {
      const loadMFAStatus = async () => {
        const status = await checkMFAStatus();
        setMfaStatus(status);

        // Auto-close if MFA is already enabled
        if (status.enabled) {
          onOpenChange(false);
        }
      };
      loadMFAStatus();
    }
  }, [open, checkMFAStatus, onOpenChange]);

  const handleSecureNow = () => {
    setShowSetup(true);
  };

  const handleRemindLater = () => {
    dismissReminder(true);
    onOpenChange(false);
  };

  const handleSetupComplete = async () => {
    // Check if MFA was successfully enabled
    const status = await checkMFAStatus();
    if (status.enabled) {
      setShowSetup(false);
      onOpenChange(false);
      // Dismiss the reminder permanently since MFA is now enabled
      dismissReminder(false);
    }
  };

  if (showSetup) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          hideCloseButton
          className="light font-archivo max-w-[95vw] sm:max-w-[600px] max-h-[90vh] overflow-y-auto gap-0 rounded-[3px] border border-[#1E1B4B]/[0.12] bg-white p-0"
        >
          <div className="flex items-start gap-[11px] border-b border-[#1E1B4B]/[0.12] px-6 py-[22px]">
            <Lock size={20} strokeWidth={1.6} className="flex-shrink-0 text-[#1E1B4B]" />
            <span className="flex flex-col gap-[5px]">
              <span className="font-archivo text-[17px] font-bold leading-[1.3] text-[#1E1B4B]">
                Secure Your Account
              </span>
              <span className="font-archivo text-[13px] leading-relaxed text-[#6E6B8A]">
                Set up two-factor authentication to protect your account
              </span>
            </span>
          </div>

          <div className="px-6 py-[22px]">
            <ProfileSecurityForm />
          </div>

          <div className="flex justify-end gap-[10px] border-t border-[#1E1B4B]/[0.12] px-6 py-4">
            <button
              type="button"
              onClick={() => setShowSetup(false)}
              className="flex items-center justify-center rounded-[2px] border border-[#1E1B4B]/[0.22] px-5 py-[14px] font-archivo text-sm font-semibold text-[#4A4770] hover:bg-[#F7F7FB]"
            >
              Back
            </button>
            <button
              type="button"
              onClick={handleSetupComplete}
              className="flex items-center justify-center rounded-[2px] bg-[#5A31D8] px-6 py-[14px] font-archivo text-sm font-semibold text-white hover:bg-[#4526A8]"
            >
              Done
            </button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        hideCloseButton
        className="light font-archivo max-w-[95vw] sm:max-w-[480px] gap-0 rounded-[3px] border border-[#1E1B4B]/[0.12] bg-white p-[26px] pb-[22px]"
      >
        <span className="font-archivo text-[19px] font-bold leading-[1.25] text-[#1E1B4B]">
          Secure Your Account
        </span>
        <p className="mt-2 font-archivo text-sm leading-[1.6] text-[#6E6B8A]">
          Add an extra layer of security with two-factor authentication
        </p>

        <div className="mt-6 flex justify-end gap-[10px]">
          <button
            type="button"
            onClick={handleRemindLater}
            className="flex items-center justify-center rounded-[2px] border border-[#1E1B4B]/[0.22] px-[18px] py-[14px] font-archivo text-sm font-semibold text-[#4A4770] hover:bg-[#F7F7FB]"
          >
            Remind Me Later
          </button>
          <button
            type="button"
            onClick={handleSecureNow}
            className="flex items-center justify-center gap-2 rounded-[2px] bg-[#5A31D8] px-[18px] py-[14px] font-archivo text-sm font-semibold text-white hover:bg-[#4526A8]"
          >
            <Lock className="h-4 w-4" strokeWidth={1.8} />
            Secure Account Now
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
